import axios from "axios";
import { baseurl } from "../Basurl/Baseurl";

const getAuthHeaders = (isMultipart = false) => {
  const token = localStorage.getItem("token") || "";
  const headers = {
    Authorization: `Bearer ${token}`,
  };
  if (!isMultipart) {
    headers["Content-Type"] = "application/json";
  } else {
    headers["Content-Type"] = "multipart/form-data";
  }
  return headers;
};

/**
 * Fetch Physiotherapy KPI dashboard statistics
 */
export const fetchPhysiotherapyStats = async () => {
  try {
    const res = await axios.get(`${baseurl}physiotherapy/dashboard-stats`, {
      headers: getAuthHeaders(),
    });
    return res.data?.data || res.data || {};
  } catch (error) {
    console.error("Error fetching physiotherapy stats:", error);
    throw error.response?.data?.message || error.message || "Failed to load stats";
  }
};

/**
 * Fetch list of physiotherapy cases / worklist with filters and pagination
 */
export const fetchPhysiotherapyPatients = async (params = {}) => {
  try {
    const query = new URLSearchParams();
    Object.keys(params).forEach((key) => {
      if (params[key] !== undefined && params[key] !== null && params[key] !== "") {
        query.append(key, params[key]);
      }
    });

    const res = await axios.get(
      `${baseurl}physiotherapy/patients?${query.toString()}`,
      {
        headers: getAuthHeaders(),
      }
    );
    return res.data || { data: [], pagination: {} };
  } catch (error) {
    console.error("Error fetching physiotherapy patient list:", error);
    throw (
      error.response?.data?.message ||
      error.message ||
      "Failed to load physiotherapy cases"
    );
  }
};

/**
 * Fetch detailed physiotherapy case by ID
 */
export const fetchPhysiotherapyDetails = async (caseId) => {
  try {
    const res = await axios.get(`${baseurl}physiotherapy/details/${caseId}`, {
      headers: getAuthHeaders(),
    });
    return res.data?.data || res.data;
  } catch (error) {
    console.error("Error fetching physiotherapy case details:", error);
    throw (
      error.response?.data?.message ||
      error.message ||
      "Failed to load case details"
    );
  }
};

/**
 * Fetch all physiotherapy cases for a specific patient
 */
export const fetchPhysiotherapyByPatient = async (patientId) => {
  try {
    const res = await axios.get(
      `${baseurl}physiotherapy/patient/${patientId}`,
      {
        headers: getAuthHeaders(),
      }
    );
    return res.data?.data || res.data || [];
  } catch (error) {
    console.error("Error fetching physiotherapy by patient ID:", error);
    throw (
      error.response?.data?.message ||
      error.message ||
      "Failed to load patient physiotherapy history"
    );
  }
};

/**
 * Create a new physiotherapy referral / case
 */
export const createPhysiotherapyReferral = async (data) => {
  try {
    const isFormData = typeof FormData !== "undefined" && data instanceof FormData;
    const res = await axios.post(`${baseurl}physiotherapy/create`, data, {
      headers: getAuthHeaders(isFormData),
    });
    return res.data;
  } catch (error) {
    console.error("Error creating physiotherapy referral:", error);
    throw (
      error.response?.data?.message ||
      error.message ||
      "Failed to create physiotherapy referral"
    );
  }
};

/**
 * Update case details (diagnosis, goals, prescribed sessions, etc.)
 */
export const updatePhysiotherapyCase = async (caseId, data) => {
  const isFormData = typeof FormData !== "undefined" && data instanceof FormData;
  const headers = getAuthHeaders(isFormData);
  const endpoints = [
    `${baseurl}physiotherapy/update/${caseId}`,
    `${baseurl}physiotherapy/${caseId}`,
    `${baseurl}update_physiotherapy/${caseId}`,
  ];

  let lastError = null;
  for (const url of endpoints) {
    try {
      const res = await axios.put(url, data, { headers });
      return res.data;
    } catch (error) {
      lastError = error;
      if (error.response?.status === 404) {
        // try next endpoint alias
        continue;
      }
      break;
    }
  }

  console.error("Error updating physiotherapy case:", lastError);
  throw (
    lastError?.response?.data?.message ||
    lastError?.message ||
    "Failed to update physiotherapy case"
  );
};

/**
 * Update physiotherapy case status (Referred, In Progress, Completed, On Hold, Cancelled)
 */
export const updatePhysiotherapyStatus = async (caseId, status, notes = "") => {
  try {
    const res = await axios.put(
      `${baseurl}physiotherapy/status/${caseId}`,
      { status, notes },
      {
        headers: getAuthHeaders(),
      }
    );
    return res.data;
  } catch (error) {
    console.error("Error updating physiotherapy status:", error);
    throw (
      error.response?.data?.message ||
      error.message ||
      "Failed to update case status"
    );
  }
};

/**
 * Add a new physiotherapy session
 */
export const addPhysiotherapySession = async (caseId, sessionData) => {
  try {
    const isFormData =
      typeof FormData !== "undefined" && sessionData instanceof FormData;
    const res = await axios.post(
      `${baseurl}physiotherapy/session/${caseId}`,
      sessionData,
      {
        headers: getAuthHeaders(isFormData),
      }
    );
    return res.data;
  } catch (error) {
    console.error("Error adding physiotherapy session:", error);
    throw (
      error.response?.data?.message ||
      error.message ||
      "Failed to record session"
    );
  }
};

/**
 * Update an existing session
 */
export const updatePhysiotherapySession = async (
  caseId,
  sessionId,
  sessionData
) => {
  const isFormData =
    typeof FormData !== "undefined" && sessionData instanceof FormData;
  const headers = getAuthHeaders(isFormData);
  const endpoints = [
    `${baseurl}physiotherapy/session/${caseId}/${sessionId}`,
    `${baseurl}physiotherapy/session/update/${caseId}/${sessionId}`,
    `${baseurl}update_physiotherapy_session/${caseId}/${sessionId}`,
    `${baseurl}update_physiotherapy_session/${caseId}`,
  ];

  let lastError = null;
  for (const url of endpoints) {
    try {
      const res = await axios.put(url, sessionData, { headers });
      return res.data;
    } catch (error) {
      lastError = error;
      if (error.response?.status === 404) {
        // try next endpoint alias
        continue;
      }
      break;
    }
  }

  console.error("Error updating physiotherapy session:", lastError);
  throw (
    lastError?.response?.data?.message ||
    lastError?.message ||
    "Failed to update session"
  );
};

/**
 * Delete a session
 */
export const deletePhysiotherapySession = async (caseId, sessionId) => {
  try {
    const res = await axios.delete(
      `${baseurl}physiotherapy/session/${caseId}/${sessionId}`,
      {
        headers: getAuthHeaders(),
      }
    );
    return res.data;
  } catch (error) {
    console.error("Error deleting physiotherapy session:", error);
    throw (
      error.response?.data?.message ||
      error.message ||
      "Failed to delete session"
    );
  }
};

/**
 * Add clinical note to case
 */
export const addPhysiotherapyNote = async (caseId, noteData) => {
  try {
    const isFormData =
      typeof FormData !== "undefined" && noteData instanceof FormData;
    const res = await axios.post(
      `${baseurl}physiotherapy/notes/${caseId}`,
      noteData,
      {
        headers: getAuthHeaders(isFormData),
      }
    );
    return res.data;
  } catch (error) {
    console.error("Error adding clinical note:", error);
    throw (
      error.response?.data?.message ||
      error.message ||
      "Failed to add clinical note"
    );
  }
};

/**
 * Upload rehab document(s) to case
 */
export const uploadPhysiotherapyDocuments = async (caseId, formData) => {
  try {
    const res = await axios.post(
      `${baseurl}physiotherapy/documents/${caseId}`,
      formData,
      {
        headers: getAuthHeaders(true),
      }
    );
    return res.data;
  } catch (error) {
    console.error("Error uploading physiotherapy documents:", error);
    throw (
      error.response?.data?.message ||
      error.message ||
      "Failed to upload document"
    );
  }
};

// Clinical Category Options
export const PHYSIO_CATEGORIES = [
  "Orthopedic",
  "Neurological",
  "Post-Op Rehab",
  "Sports Injury",
  "Spine & Back Care",
  "Pediatric",
  "Geriatric",
  "Cardiopulmonary",
  "General",
  "Other",
];

// Status Options
export const PHYSIO_STATUSES = [
  "Referred",
  "Assessment Pending",
  "In Progress",
  "Completed",
  "On Hold",
  "Cancelled",
];

// Priority Options
export const PHYSIO_PRIORITIES = ["Normal", "Urgent", "High", "Low"];

// Modalities Options
export const PHYSIO_MODALITIES = [
  "Cryotherapy (Ice Pack)",
  "Heat Therapy / Moist Heat",
  "TENS (Electrical Stimulation)",
  "Ultrasound Therapy",
  "Traction (Cervical / Lumbar)",
  "Manual Joint Mobilization",
  "Myofascial Release",
  "Laser Therapy",
  "Dry Needling",
  "Kinesio Taping",
  "Resistance Band Exercises",
  "Static Cycling",
];
